import * as React from "react";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Spinner } from "./ui/spinner";
import { Link, useSearch } from "@tanstack/react-router";
import { AuthFrame } from "./auth-frame";
import { getClient } from "@knowsuchagency/django-allauth";
import { saveTokens } from "@/auth";
import { useValidatePassword } from "@/user/api";
import { Check, X } from "lucide-react";
import { useDebounce } from "use-debounce";

const formSchema = z.object({
  email: z.string().email({
    message: "Please enter a valid email address",
  }),
  password1: z.string().min(8, {
    message: "Password must be at least 8 characters",
  }),
  password2: z.string().min(8, {
    message: "Password must be at least 8 characters",
  }),
}).refine((data) => data.password1 === data.password2, {
  message: "Passwords don't match",
  path: ["password2"],
});

interface SignupProps extends React.HTMLAttributes<HTMLDivElement> { }

export function Signup({ className, ...props }: SignupProps) {
  const client = getClient();
  const search = useSearch({ from: '/signup' });
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [validationErrors, setValidationErrors] = React.useState<string[]>([]);
  const validatePassword = useValidatePassword();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password1: "",
      password2: "",
    },
  });

  const password1 = form.watch("password1");
  const password2 = form.watch("password2");
  const email = form.watch("email");

  const [debouncedPassword] = useDebounce(password1, 500);
  const [debouncedEmail] = useDebounce(email, 500);

  // Validate password when debounced values change
  React.useEffect(() => {
    if (debouncedPassword) {
      validatePassword.mutate(
        { password: debouncedPassword, email: debouncedEmail || undefined },
        {
          onSuccess: (data) => {
            setValidationErrors(data.errors || []);
          },
          onError: () => {
            setValidationErrors([]);
          },
        }
      );
    } else {
      setValidationErrors([]);
    }
  }, [debouncedPassword, debouncedEmail]);

  const passwordsMatch = password1 && password2 && password1 === password2;

  const requirements = [
    {
      code: "password_too_short",
      label: "At least 8 characters",
      met: !validationErrors.includes("password_too_short") && password1.length >= 8,
    },
    {
      code: "password_too_similar",
      label: "Not too similar to your email",
      met: !validationErrors.includes("password_too_similar"),
    },
    {
      code: "password_too_common",
      label: "Not a commonly used password",
      met: !validationErrors.includes("password_too_common"),
    },
    {
      code: "password_entirely_numeric",
      label: "Not entirely numeric",
      met: !validationErrors.includes("password_entirely_numeric"),
    },
    {
      code: "passwords_match",
      label: "Passwords match",
      met: passwordsMatch,
    },
  ];

  const allRequirementsMet = requirements.every((req) => req.met);

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      // Use the client directly to get the response data
      const response = await client.signup({
        email: values.email,
        password: values.password1,
      });

      // Determine redirect URL from search params or use fallback
      const redirectUrl = search.redirect || '/organization';

      // Handle the response based on authentication status
      if (response.status === 200 && 'user' in response.data) {
        // User is authenticated, extract tokens
        const userData: any = response.data.user;
        const access = userData?.access;
        const refresh = userData?.refresh;

        if (access && refresh) {
          saveTokens({ access, refresh });
          window.location.href = redirectUrl;
        } else {
          // Authenticated but no tokens in response - use session
          window.location.href = redirectUrl;
        }
      } else if (response.status === 401) {
        // Additional authentication steps needed (e.g., email verification)
        // The response.data.flows will indicate what's needed
        window.location.href = redirectUrl;
      }
    } catch (error: any) {
      console.log({ error })
      // Handle allauth error format
      const errorData = error?.response?.data;
      if (errorData?.errors) {
        // Set field-specific errors
        Object.keys(errorData.errors).forEach((field) => {
          const messages = errorData.errors[field];
          if (Array.isArray(messages) && messages.length > 0) {
            form.setError(field as any, {
              type: "server",
              message: messages[0].message || messages[0],
            });
          }
        });
      } else {
        form.setError("root", {
          type: "server",
          message: errorData?.detail || "Signup failed"
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthFrame>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-6 w-[350px]">
            <div className="grid gap-2">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <label className="text-white" htmlFor="email">Email</label>
                    <FormControl>
                      <Input placeholder="Email" type="email" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password1"
                render={({ field }) => (
                  <FormItem>
                    <label className="text-white" htmlFor="password1">Password</label>
                    <FormControl>
                      <Input
                        placeholder="Password"
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password2"
                render={({ field }) => (
                  <FormItem>
                    <label className="text-white" htmlFor="password2">Confirm Password</label>
                    <FormControl>
                      <Input
                        placeholder="Confirm Password"
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* Password Requirements Checklist */}
              {password1 && (
                <div className="space-y-2 text-sm">
                  <p className="text-white font-medium">Password requirements:</p>
                  {requirements.map((req) => (
                    <div key={req.code} className="flex items-center gap-2">
                      {req.met ? (
                        <Check className="h-4 w-4 text-green-500" />
                      ) : (
                        <X className="h-4 w-4 text-red-500" />
                      )}
                      <span className={req.met ? "text-green-500" : "text-gray-400"}>
                        {req.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <Button
              type="submit"
              disabled={!form.formState.isValid || isSubmitting || !allRequirementsMet}
              variant="secondary"
            >
              {isSubmitting ? (
                <Spinner className="h-6 w-6" />
              ) : (
                "Sign up"
              )}
            </Button>
            <Button type="button" variant="default">
              <Link to="/login" search={(prev) => prev}>Already have an account? Sign in</Link>
            </Button>
            <p
              className={cn("text-sm font-medium text-destructive", className)}
            >
              {form.formState.errors.root?.message}
            </p>

            {/* Divider */}
            {/*<div className="relative">*/}
            {/*  <div className="absolute inset-0 flex items-center">*/}
            {/*    <span className="w-full border-t border-gray-300" />*/}
            {/*  </div>*/}
            {/*  <div className="relative flex justify-center text-xs uppercase">*/}
            {/*    <span className="bg-white px-2 text-gray-500">Or continue with</span>*/}
            {/*  </div>*/}
            {/*</div>*/}

            {/*/!* Social Login Buttons *!/*/}
            {/*<div className="flex flex-row gap-2">*/}
            {/*  <Button*/}
            {/*    type="button"*/}
            {/*    variant="outline"*/}
            {/*    onClick={() => redirectToProvider('github', '/auth/callback', 'signup')}*/}
            {/*  >*/}
            {/*    <svg className="mr-2 h-4 w-4" aria-hidden="true" focusable="false" data-prefix="fab" data-icon="github" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 496 512">*/}
            {/*      <path fill="currentColor" d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8zM97.2 352.9c-1.3 1-1 3.3.7 5.2 1.6 1.6 3.9 2.3 5.2 1 1.3-1 1-3.3-.7-5.2-1.6-1.6-3.9-2.3-5.2-1zm-10.8-8.1c-.7 1.3.3 2.9 2.3 3.9 1.6 1 3.6.7 4.3-.7.7-1.3-.3-2.9-2.3-3.9-2-.6-3.6-.3-4.3.7zm32.4 35.6c-1.6 1.3-1 4.3 1.3 6.2 2.3 2.3 5.2 2.6 6.5 1 1.3-1.3.7-4.3-1.3-6.2-2.2-2.3-5.2-2.6-6.5-1zm-11.4-14.7c-1.6 1-1.6 3.6 0 5.9 1.6 2.3 4.3 3.3 5.6 2.3 1.6-1.3 1.6-3.9 0-6.2-1.4-2.3-4-3.3-5.6-2z"></path>*/}
            {/*    </svg>*/}
            {/*    GitHub*/}
            {/*  </Button>*/}
            {/*</div>*/}
          </div>
        </form>
      </Form>
    </AuthFrame>
  );
}
